import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  ListItemButton,
  ListItemIcon,
  Breadcrumbs,
  Paper,
  Radio,
  FormControlLabel,
  RadioGroup,
  Checkbox,
  TextField,
  Divider,
} from "@mui/material";
import { Bento, WarningAmber } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { themes, sideBarWidth, theme } from "services/constants";
import { ThemeProvider } from "@mui/material/styles";
import { CustomButton } from "components/CustomComponents";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";

export const EODSettings = () => {
  const { domain, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer,
  );

  const isAllCompaniesSelected = Number(topbarCompanyId) === 99999;

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [exists, setExists] = useState(false);

  const [formData, setFormData] = useState({
    report_type: "basic",
    auto_switch_mode: false,
    submission_deadline: dayjs("2026-02-06T18:00"),
    reminder_method: "in_app",
    reminder_buffer_mins: 30,
  });

  useEffect(() => {
    if (!topbarCompanyId || isAllCompaniesSelected) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      setLoading(true);

      try {
        const response = await customGetAPI(
          `${domain}eod-settings/?company_id=${topbarCompanyId}`,
        );

        if (
          response.ok &&
          response.responseDetails?.data &&
          Object.keys(response.responseDetails.data).length > 0
        ) {
          const fetched = response.responseDetails.data;
          setFormData({
            report_type: fetched.report_type || "basic",
            auto_switch_mode: fetched.auto_switch_mode || false,
            submission_deadline: dayjs(
              `2026-01-01T${fetched.submission_deadline || "18:00"}`,
            ),
            reminder_method: fetched.reminder_method || "in_app",
            reminder_buffer_mins: fetched.reminder_buffer_mins || 30,
          });
          setExists(true);
        } else {
          setExists(false);
        }
      } catch (err) {
        console.error("Fetch error", err);
        setExists(false);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [topbarCompanyId, domain, isAllCompaniesSelected]);

  const handleSave = async () => {
    if (isAllCompaniesSelected) {
      showToast("error", "Action not allowed for 'All Companies' view.");
      return;
    }

    setSaveLoading(true);
    const payload = {
      company_id: topbarCompanyId,
      report_type: formData.report_type,
      auto_switch_mode: formData.auto_switch_mode,
      submission_deadline: formData.submission_deadline.format("HH:mm"),
      reminder_method: formData.reminder_method,
    };

    if (formData.report_type === "project") {
      payload.reminder_buffer_mins = Number(formData.reminder_buffer_mins);
    }

    try {
      const method = exists ? "PUT" : "POST";
      const response = await customAPI(
        `${domain}eod-settings/`,
        method,
        payload,
      );

      if (response.ok) {
        showToast(
          "success",
          `Settings ${exists ? "updated" : "created"} successfully!`,
        );
        setExists(true);
      } else {
        showToast("error", "Failed to save settings. Please try again.");
      }
    } catch (err) {
      showToast("error", "An error occurred while saving.");
    } finally {
      setSaveLoading(false);
    }
  };

  const cardStyle = (active) => ({
    border: `1.5px solid ${active ? "#00D4AA" : "#e0e0e0"}`,
    borderRadius: "10px",
    p: 3,
    mb: 2,
    cursor: "pointer",
    backgroundColor: active ? "#f6fffb" : "white",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
  });

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            backgroundColor: "#f5f5fd",
            overflow: "hidden",
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: sideBarWidth + 60,
              flexShrink: 0,
              backgroundColor: "white",
              borderRight: "1px solid #eef0f2",
              p: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: "#00D4AA", mb: 2, px: 1 }}
            >
              Settings
            </Typography>
            <ListItemButton
              sx={{ backgroundColor: "#f0f8ff", borderRadius: 2, p: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "#00D4AA" }}>
                <Bento />
              </ListItemIcon>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  EOD Settings
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Define EOD settings
                </Typography>
              </Box>
            </ListItemButton>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              p: 3,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
                <Typography variant="body2">Administration</Typography>
                <Typography variant="body2">Settings</Typography>
                <Typography variant="body2" color={themes?.blueColor}>
                  EOD Settings
                </Typography>
              </Breadcrumbs>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    EOD Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Configure EOD reporting format, deadline, and reminders
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
              }}
            >
              {isAllCompaniesSelected ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    borderRadius: "16px",
                    border: "1px dashed #00D4AA",
                    textAlign: "center",
                    backgroundColor: "white",
                    mt: 0,
                  }}
                >
                  <WarningAmber
                    sx={{ fontSize: 80, color: "#00D4AA", mb: 2 }}
                  />
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Company Selection Required
                  </Typography>
                  <Typography color="text.secondary" variant="body1">
                    EOD Settings cannot be configured in the{" "}
                    <b>'All Companies'</b> view. Please select a specific
                    company from the top bar to continue.
                  </Typography>
                </Paper>
              ) : loading ? (
                <Loader />
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: "16px",
                    border: "1px solid #eef0f2",
                    backgroundColor: "white",
                    mt: 0,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    EOD Report Settings
                  </Typography>

                  <Typography sx={{ fontWeight: 600, mb: 2 }}>
                    EOD Report Type
                  </Typography>
                  <RadioGroup
                    value={formData.report_type}
                    onChange={(e) =>
                      setFormData({ ...formData, report_type: e.target.value })
                    }
                  >
                    <Box
                      sx={cardStyle(formData.report_type === "basic")}
                      onClick={() =>
                        setFormData({ ...formData, report_type: "basic" })
                      }
                    >
                      <Radio value="basic" color="success" sx={{ mt: -0.5 }} />
                      <Box sx={{ ml: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>
                          Basic EOD
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Simple reporting format. Employees manually list
                          tasks.
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={cardStyle(formData.report_type === "project")}
                      onClick={() =>
                        setFormData({ ...formData, report_type: "project" })
                      }
                    >
                      <Radio
                        value="project"
                        color="success"
                        sx={{ mt: -0.5 }}
                      />
                      <Box sx={{ ml: 1, width: "100%" }}>
                        <Typography sx={{ fontWeight: 700 }}>
                          Project-Linked EOD
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Auto-syncs with tasks from the project management
                          module.
                        </Typography>
                        {formData.report_type === "project" && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.auto_switch_mode}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    auto_switch_mode: e.target.checked,
                                  })
                                }
                                color="success"
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Auto-detect and switch mode based on project
                                assignment.
                              </Typography>
                            }
                            sx={{ mt: 2, ml: 0 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </RadioGroup>

                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Notification & Compliance
                  </Typography>

                  {/* Deadline Row */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: { md: "center" },
                      mb: 3,
                      gap: 4,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        EOD Submission Deadline
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        The cutoff time after which reports are marked late.
                      </Typography>
                    </Box>
                    <TimePicker
                      value={formData.submission_deadline}
                      onChange={(val) =>
                        setFormData({ ...formData, submission_deadline: val })
                      }
                      sx={{ width: { xs: "100%", md: 320 } }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      alignItems: { md: "center" },
                      mb: 3,
                      gap: 4,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Reminder Method
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Choose the notification channel.
                      </Typography>
                    </Box>
                    <RadioGroup
                      row
                      value={formData.reminder_method}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          reminder_method: e.target.value,
                        })
                      }
                      sx={{ width: { xs: "100%", md: 320 } }}
                    >
                      <FormControlLabel
                        value="in_app"
                        control={<Radio color="success" size="small" />}
                        label="In-App"
                      />
                      <FormControlLabel
                        value="email"
                        control={<Radio color="success" size="small" />}
                        label="Email"
                      />
                      <FormControlLabel
                        value="both"
                        control={<Radio color="success" size="small" />}
                        label="Both"
                      />
                    </RadioGroup>
                  </Box>

                  {formData.report_type === "project" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { md: "center" },
                        mb: 3,
                        gap: 4,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600 }}>
                          Send Reminder Before Deadline
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Minutes before deadline to notify.
                        </Typography>
                      </Box>
                      <TextField
                        type="number"
                        value={formData.reminder_buffer_mins}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            reminder_buffer_mins: e.target.value,
                          })
                        }
                        placeholder="e.g. 45"
                        sx={{ width: { xs: "100%", md: 320 } }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                  >
                    <CustomButton
                      actionFuntion={handleSave}
                      actionName={saveLoading ? "Saving..." : "Save"}
                      typeName="button"
                      disableAction={saveLoading}
                    />
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};
